"use client";
import { createAppKit, CaipNetwork } from "@reown/appkit/react";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import {
  AppKitNetwork,
  arbitrum,
  base,
  mainnet,
  optimism,
  polygon,
  sepolia,
  bsc,
  solana as solanaNetwork,
  zksync,
  sonic,
  opBNB,
  linea,
  unichain,
  mantle,
  berachain,
  ronin,
  avalanche,
  baseSepolia,
  bscTestnet,
} from "@reown/appkit/networks";
import { PropsWithChildren } from "react";
import { http, parseCookie, WagmiProvider, type Config } from "wagmi";
import { createStorage } from "@wagmi/core";
import { SolanaAdapter } from "@reown/appkit-adapter-solana";
import log from "logbeacon";

// Set up queryClient

// const projectId = "a4598f454c35e63eefd0ee88ea2c642c";
const getProjectId = () => {
  if (process.env.NEXT_PUBLIC_REOWN_PROJECT_ID) {
    return process.env.NEXT_PUBLIC_REOWN_PROJECT_ID;
  }
  const env = process.env.NEXT_PUBLIC_ENV;
  switch (env) {
    case "dev":
    case "beta":
      return "6c751343963f5357d7546a53fb45f477";
    case "pre":
    case "prod":
      return "6c751343963f5357d7546a53fb45f477";
    default:
      return "6c751343963f5357d7546a53fb45f477";
  }
};
const projectId = getProjectId();

if (!projectId) {
  throw new Error("Project ID is not defined");
}

// Set up metadata
const metadata = {
  name: "EdWealth Swapper",
  description:
    "EdWealth Swap is a decentralized exchange that allows you to swap tokens",
  url: "http://edwealth.ai", // origin must match your domain & subdomain
  icons: ["https://swap.edwealth.ai/edwealth192.png"],
};

// 修改 Solana 网络的 RPC 节点

const solana = {
  ...solanaNetwork,
  rpcUrls: {
    ...solanaNetwork.rpcUrls,
    default: {
      http: [process.env.NEXT_PUBLIC_SOLANA_RPC_URL as string],
    },
  },
} as CaipNetwork;

// Solana base bsc
const networks: [AppKitNetwork, ...AppKitNetwork[]] = [
  mainnet, // Ethereum Mainnet
  arbitrum, // Arbitrum
  base, // Base
  berachain, // Berachain
  optimism, // Optimism
  polygon, // Polygon
  avalanche, // Avalanche
  zksync, // zkSync
  linea, // Linea
  mantle, // Mantle
  opBNB, // opBNB
  ronin, // Ronin
  unichain, // Unichain
  sonic, // Sonic
  sepolia, // Sepolia (测试网)
  solana,
  // bsc 必须放后面, phantom 会报错
  bsc, // BNB Smart Chain
  baseSepolia, // base (测试网)
  bscTestnet, // bsc （测试网）
];
log.debug(
  "[文案]networks",
  networks.map((network) => {
    return network.id;
  }),
);

const solanaWeb3JsAdapter = new SolanaAdapter();
//Set up the Wagmi Adapter (Config)
const wagmiAdapter = new WagmiAdapter({
  storage: createStorage({
    storage: {
      getItem: (key: string) => {
        log.debug("[文案]storage-setItem", key);

        if (typeof window === "undefined") {
          return null;
        }
        const value = parseCookie(document.cookie, key);
        log.debug("[文案]storage-getItem", key);
        return value ?? null;
      },
      setItem: (key: string, value: string) => {
        log.debug("[文案]storage-setItem", key, value);
        if (typeof window === "undefined") {
          return;
        }
        document.cookie = `${key}=${value};path=/;SameSite=None; Secure`;
      },
      removeItem: (key: string) => {
        log.debug("[文案]storage-removeItem", key);
        if (typeof window === "undefined") {
          return;
        }
        document.cookie = `${key}=;max-age=-1;path=/;SameSite=None; Secure`;
      },
    },
  }),
  ssr: true,
  projectId,
  networks,
  // An unknown RPC error occurred. Add explicit http transport configuration https://github.com/reown-com/appkit/issues/4144
  transports: {
    [unichain.id]: http(),
    [optimism.id]: http(),
    [berachain.id]: http(),
    [ronin.id]: http(),
    [sonic.id]: http(),
  },
});

createAppKit({
  adapters: [wagmiAdapter, solanaWeb3JsAdapter],
  metadata,
  networks,
  defaultNetwork: mainnet,
  projectId,
  features: {
    analytics: true, // Optional - defaults to your Cloud configuration
    email: false,
    socials: false,
  },
  featuredWalletIds: [
    "c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96", // MetaMask
    "4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0", // Rainbow
    "225affb176778569276e484e1b92637ad061b01e13a048b35a9d280c3b58970f", // OKX
    "971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709", // Trust
  ],
  includeWalletIds: [
    "971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709",
    "38f5d18bd8522c244bdd70cb4a68e0e718865155811c043f052fb9f1c51de662",
    "8a0ee50d1f22f6651afcae7eb4253e52a3310b90af5daef78a8c4929a9bb99d4",
    "15c8b91ade1a4e58f3ce4e7a0dd7f42b47db0c8df7e0d84f63eb39bcb96c4e0f",
    "fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa",
    "2a3c89040ac3b723a1972a33a125b1db11e258a6975d3a61252cd64e6ea5ea01",
    "2bd8c14e035c2d48f184aaa168559e86b0e3433228d3c4075900a221785019b0",
    "20459438007b75f4f4acb98bf29aa3b800550309646d375da5fd4aac6c2a2c66",
    "18388be9ac2d02726dbac9777c96efaac06d744b2f6d580fccdd4127a6d01fd1",
    "c03dfee351b6fcc421b4494ea33b9d4b92a984f87aa76d1663bb28705e95034a",
    "c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96",
    "19177a98252e07ddfc9af2083ba8e07ef627cb6103467ffebb3f8f4205fd7927",
    "225affb176778569276e484e1b92637ad061b01e13a048b35a9d280c3b58970f",
    "4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0",
    "0b415a746fb9ee99cce155c2ceca0c6f6061b1dbca2d722b3ba16381d0562150",
    "1ae92b26df02f0abca6304df07debccd18262fdf5fe82daa81593582dac9a369",
    "ef333840daf915aafdc4a004525502d6d49d77bd9c65e0642dbaefb3c2893bef",
  ],
});

function deserialize<type>(value: string): type {
  return JSON.parse(value, (_, value_) => {
    let value = value_;
    if (value?.__type === "bigint") {
      value = BigInt(value.value);
    }
    if (value?.__type === "Map") {
      value = new Map(value.value);
    }
    return value;
  });
}

export function AppKitRegistry({
  children,
  cookies,
}: PropsWithChildren<{ cookies: Record<string, string> | null }>) {
  const config = wagmiAdapter.wagmiConfig;
  const key = `${config.storage?.key}.store`;
  const parsed = cookies?.[key];

  const initialState = parsed ? deserialize<any>(parsed).state : undefined;

  return (
    <WagmiProvider
      config={wagmiAdapter.wagmiConfig as Config}
      initialState={initialState}
    >
      {children}
    </WagmiProvider>
  );
}
